import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import {
  AuthoringAssistantChatResponse,
  AuthoringAssistantConversationMessage,
  AuthoringAssistantDraftCourse,
  AuthoringAssistantDraftExercise,
  AuthoringAssistantDraftLesson,
  AuthoringAssistantDraftMaterial,
  AuthoringAssistantDraftTarget,
  AuthoringAssistantGeneratedDraftResponse,
  AuthoringAssistantQuickAction,
  chatWithAuthoringAssistant,
  generateAuthoringAssistantDraft,
} from '../../services/authoringAssistantService';

interface AuthoringAssistantPanelProps {
  courseId?: number;
  lessonId?: number;
  materialId?: number;
  exerciseId?: number;
  draftCourse?: AuthoringAssistantDraftCourse;
  draftLesson?: AuthoringAssistantDraftLesson;
  draftMaterial?: AuthoringAssistantDraftMaterial;
  draftExercise?: AuthoringAssistantDraftExercise;
  draftTarget?: AuthoringAssistantDraftTarget;
  draftButtonLabel?: string;
  improveButtonLabel?: string;
  draftDisabled?: boolean;
  onApplyDraft?: (draft: AuthoringAssistantGeneratedDraftResponse) => void;
  initialPrompt?: string;
  autoGenerateInitialDraft?: boolean;
  createActions?: Array<{
    id: string;
    label: string;
    onCreate: (prompt?: string) => void;
  }>;
}

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
}

const AUTHORING_PANEL_OPEN_STORAGE_KEY = 'kipina-panel-open';
const AUTHORING_PANEL_WIDTH_STORAGE_KEY = 'kipina-panel-width';
const DEFAULT_PANEL_WIDTH = 420;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 640;
const COLLAPSED_PANEL_WIDTH = 74;
const RESIZE_HANDLE_WIDTH = 22;

const clampPanelWidth = (width: number) => Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width));

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const renderInlineMarkdown = (value: string) => escapeHtml(value)
  .replace(/`([^`]+)`/g, '<code>$1</code>')
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/__([^_]+)__/g, '<strong>$1</strong>')
  .replace(/(^|\W)\*([^*]+)\*(?=\W|$)/g, '$1<em>$2</em>')
  .replace(/(^|\W)_([^_]+)_(?=\W|$)/g, '$1<em>$2</em>');

const isMarkdownTableSeparator = (value: string) => /^\|?(\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?$/.test(value.trim());

const parseMarkdownTableCells = (value: string) => value
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim());

const renderMarkdownToHtml = (value: string) => {
  const lines = value.replace(/\r\n/g, '\n').split('\n');
  const htmlParts: string[] = [];
  let paragraphLines: string[] = [];
  let blockquoteLines: string[] = [];
  const listStack: Array<{
    type: 'ul' | 'ol';
    indent: number;
    hasOpenItem: boolean;
  }> = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return;
    }

    htmlParts.push(`<p>${paragraphLines.map((line) => renderInlineMarkdown(line)).join('<br />')}</p>`);
    paragraphLines = [];
  };

  const closeListLevel = () => {
    const currentLevel = listStack[listStack.length - 1];

    if (!currentLevel) {
      return;
    }

    if (currentLevel.hasOpenItem) {
      htmlParts.push('</li>');
    }

    htmlParts.push(`</${currentLevel.type}>`);
    listStack.pop();
  };

  const flushLists = () => {
    while (listStack.length > 0) {
      closeListLevel();
    }
  };

  const flushBlockquote = () => {
    if (!blockquoteLines.length) {
      return;
    }

    htmlParts.push(`<blockquote>${blockquoteLines.map((line) => renderInlineMarkdown(line)).join('<br />')}</blockquote>`);
    blockquoteLines = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushBlockquote();
    flushLists();
  };

  const appendListItem = (indent: number, type: 'ul' | 'ol', content: string) => {
    while (listStack.length > 0 && indent < listStack[listStack.length - 1].indent) {
      closeListLevel();
    }

    if (
      listStack.length > 0
      && indent === listStack[listStack.length - 1].indent
      && type !== listStack[listStack.length - 1].type
    ) {
      closeListLevel();
    }

    if (listStack.length === 0 || indent > listStack[listStack.length - 1].indent) {
      htmlParts.push(`<${type}>`);
      listStack.push({ type, indent, hasOpenItem: false });
    }

    const currentLevel = listStack[listStack.length - 1];

    if (currentLevel.hasOpenItem) {
      htmlParts.push('</li>');
    }

    htmlParts.push(`<li>${renderInlineMarkdown(content)}`);
    currentLevel.hasOpenItem = true;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushAll();
      continue;
    }

    const headingMatch = trimmedLine.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      flushAll();
      const level = headingMatch[1].length;
      htmlParts.push(`<h${level}>${renderInlineMarkdown(headingMatch[2])}</h${level}>`);
      continue;
    }

    const nextLine = lines[index + 1]?.trim() || '';
    if (trimmedLine.includes('|') && isMarkdownTableSeparator(nextLine)) {
      flushAll();

      const headerCells = parseMarkdownTableCells(trimmedLine);
      const bodyRows: string[][] = [];

      index += 2;

      while (index < lines.length) {
        const tableLine = lines[index].trim();

        if (!tableLine || !tableLine.includes('|') || isMarkdownTableSeparator(tableLine)) {
          index -= 1;
          break;
        }

        bodyRows.push(parseMarkdownTableCells(tableLine));
        index += 1;
      }

      htmlParts.push(
        `<table><thead><tr>${headerCells.map((cell) => `<th>${renderInlineMarkdown(cell)}</th>`).join('')}</tr></thead>${bodyRows.length > 0 ? `<tbody>${bodyRows.map((row) => `<tr>${headerCells.map((_, columnIndex) => `<td>${renderInlineMarkdown(row[columnIndex] || '')}</td>`).join('')}</tr>`).join('')}</tbody>` : ''}</table>`,
      );
      continue;
    }

    const listMatch = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      flushBlockquote();
      appendListItem(
        listMatch[1].replace(/\t/g, '  ').length,
        /^\d+\.$/.test(listMatch[2]) ? 'ol' : 'ul',
        listMatch[3],
      );
      continue;
    }

    const blockquoteMatch = trimmedLine.match(/^>\s?(.*)$/);
    if (blockquoteMatch) {
      flushParagraph();
      flushLists();
      blockquoteLines.push(blockquoteMatch[1]);
      continue;
    }

    flushBlockquote();
    flushLists();
    paragraphLines.push(trimmedLine);
  }

  flushAll();

  return htmlParts.join('');
};

const createMessage = (role: 'assistant' | 'user', content: string): ChatMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
});

const defaultQuickActions: AuthoringAssistantQuickAction[] = [
  {
    id: 'exercise-ideas',
    label: 'Ideoi tehtäviä',
    prompt: 'Ideoi tähän kontekstiin kolme kannustavaa ja opettavaista tehtävää.',
  },
  {
    id: 'improve-structure',
    label: 'Paranna rakennetta',
    prompt: 'Paranna tämän materiaalin tai harjoituksen rakennetta ja oppimisen etenemistä.',
  },
  {
    id: 'pedagogy-check',
    label: 'Tarkista pedagogiikka',
    prompt: 'Arvioi pedagogiikka, vaikeustaso, motivointi ja arvioitavuus.',
  },
];

const AuthoringAssistantPanel: React.FC<AuthoringAssistantPanelProps> = ({
  courseId,
  lessonId,
  materialId,
  exerciseId,
  draftCourse,
  draftLesson,
  draftMaterial,
  draftExercise,
  draftTarget,
  draftButtonLabel,
  improveButtonLabel,
  draftDisabled = false,
  onApplyDraft,
  initialPrompt,
  autoGenerateInitialDraft = false,
  createActions,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    const storedValue = window.localStorage.getItem(AUTHORING_PANEL_OPEN_STORAGE_KEY);
    return storedValue === null ? true : storedValue === 'true';
  });
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_PANEL_WIDTH;
    }

    const storedWidth = Number(window.localStorage.getItem(AUTHORING_PANEL_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) ? clampPanelWidth(storedWidth) : DEFAULT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    createMessage(
      'assistant',
      'Olen Kipinä. Autan suunnittelemaan materiaaleja ja harjoituksia niin, että niissä on sekä pedagoginen järki että vähän vetoa opiskelijalle.',
    ),
  ]);
  const [quickActions, setQuickActions] = useState<AuthoringAssistantQuickAction[]>(defaultQuickActions);
  const [input, setInput] = useState(initialPrompt || '');
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [contextSummary, setContextSummary] = useState('Kontekstissa nykyinen kurssi, oppitunti, materiaali ja/tai harjoitus');
  const [focusLabel, setFocusLabel] = useState('Suunnitteluapu');
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_PANEL_WIDTH);
  const hasAutoGeneratedDraftRef = useRef(false);

  const draftPayload = useMemo(
    () => ({
      courseId,
      lessonId,
      materialId,
      exerciseId,
      draftCourse,
      draftLesson,
      draftMaterial,
      draftExercise,
    }),
    [courseId, lessonId, materialId, exerciseId, draftCourse, draftLesson, draftMaterial, draftExercise],
  );

  const resolvedDraftButtonLabel = draftButtonLabel || {
    course: 'Kirjoita kurssiluonnos lomakkeeseen',
    lesson: 'Kirjoita oppituntiluonnos lomakkeeseen',
    material: 'Kirjoita materiaaliluonnos lomakkeeseen',
    exercise: 'Kirjoita harjoitusrunko lomakkeeseen',
  }[draftTarget || 'material'];

  const resolvedImproveButtonLabel = improveButtonLabel || {
    course: 'Tee muutokset kurssiin puolestani',
    lesson: 'Tee muutokset oppituntiin puolestani',
    material: 'Tee muutokset materiaaliin puolestani',
    exercise: 'Tee muutokset harjoitukseen puolestani',
  }[draftTarget || 'material'];

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUTHORING_PANEL_OPEN_STORAGE_KEY, String(open));
    }
  }, [open]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUTHORING_PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
    }
  }, [panelWidth]);

  useEffect(() => {
    if (isNarrow) {
      setIsResizing(false);
    }
  }, [isNarrow]);

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = resizeStartXRef.current - event.clientX;
      setPanelWidth(clampPanelWidth(resizeStartWidthRef.current + deltaX));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const applyAssistantResponse = (response: AuthoringAssistantChatResponse) => {
    const assistantMessage = createMessage('assistant', response.reply);

    setMessages((previousMessages) => [...previousMessages, assistantMessage]);
    setContextSummary(response.contextSummary);
    setFocusLabel(response.focusLabel);

    if (Array.isArray(response.quickActions) && response.quickActions.length > 0) {
      setQuickActions(response.quickActions.slice(0, 3));
    }
  };

  const sendMessage = async (message: string) => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isSending) {
      return;
    }

    const nextUserMessage = createMessage('user', trimmedMessage);
    const nextConversation = [...messages, nextUserMessage];

    setMessages(nextConversation);
    setInput('');
    setIsSending(true);

    try {
      const response = await chatWithAuthoringAssistant({
        message: trimmedMessage,
        conversationHistory: nextConversation.map<AuthoringAssistantConversationMessage>((chatMessage) => ({
          role: chatMessage.role,
          content: chatMessage.content,
        })),
        ...draftPayload,
      });

      applyAssistantResponse(response);
    } catch (error) {
      console.error('Authoring assistant chat failed:', error);
      setMessages((previousMessages) => [
        ...previousMessages,
        createMessage(
          'assistant',
          'Tuli pieni suunnittelusolmu. Kokeile sanoa tarkemmin, haluatko apua materiaaliin, tehtäväideaan vai arviointiin.',
        ),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = () => {
    void sendMessage(input);
  };

  const handleGenerateDraft = useCallback(async (
    mode: 'create' | 'improve',
    promptOverride?: string,
  ) => {
    if (!draftTarget || !onApplyDraft || draftDisabled || isGeneratingDraft) {
      return;
    }

    setIsGeneratingDraft(true);

    const resolvedPrompt = promptOverride?.trim() || input.trim() || undefined;

    try {
      const response = await generateAuthoringAssistantDraft({
        target: draftTarget,
        prompt: resolvedPrompt,
        mode,
        ...draftPayload,
      });

      onApplyDraft(response);
      setMessages((previousMessages) => [
        ...previousMessages,
        createMessage(
          'assistant',
          `${mode === 'improve' ? 'Päivitin nykyistä sisältöä lomakkeessa.' : 'Luonnos kirjoitettiin lomakkeeseen.'} ${response.summary}`,
        ),
      ]);
      setInput('');
    } catch (error) {
      console.error('Authoring assistant draft generation failed:', error);
      setMessages((previousMessages) => [
        ...previousMessages,
        createMessage(
          'assistant',
          'Luonnoksen kirjoittaminen lomakkeeseen epäonnistui. Kokeile tarkentaa pyyntöäsi tai käytä tavallista keskustelutilaa.',
        ),
      ]);
    } finally {
      setIsGeneratingDraft(false);
    }
  }, [draftDisabled, draftPayload, draftTarget, input, isGeneratingDraft, onApplyDraft]);

  useEffect(() => {
    if (!initialPrompt || input.trim()) {
      return;
    }

    setInput(initialPrompt);
  }, [initialPrompt, input]);

  useEffect(() => {
    if (
      !autoGenerateInitialDraft
      || hasAutoGeneratedDraftRef.current
      || !draftTarget
      || !onApplyDraft
      || draftDisabled
    ) {
      return;
    }

    hasAutoGeneratedDraftRef.current = true;
    void handleGenerateDraft('create', initialPrompt);
  }, [
    autoGenerateInitialDraft,
    draftDisabled,
    draftTarget,
    handleGenerateDraft,
    initialPrompt,
    onApplyDraft,
  ]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (!isSending && input.trim()) {
        handleSubmit();
      }
    }
  };

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = panelWidth;
    setIsResizing(true);
  };

  const showDesktopPanel = !isNarrow;
  const containerWidth = showDesktopPanel ? (open ? panelWidth : COLLAPSED_PANEL_WIDTH) : '100%';

  return (
    <Box
      sx={{
        width: containerWidth,
        flexShrink: 0,
        alignSelf: 'stretch',
        transition: showDesktopPanel && !isResizing ? 'width 180ms ease' : 'none',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: showDesktopPanel ? 'sticky' : 'relative',
          top: showDesktopPanel ? 24 : 0,
          height: showDesktopPanel ? 'calc(100vh - 48px)' : 'auto',
          minHeight: showDesktopPanel ? 560 : 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 4,
          border: '1px solid',
          borderColor: open ? 'rgba(14, 116, 144, 0.18)' : 'rgba(148, 163, 184, 0.24)',
          backgroundColor: '#ffffff',
          boxShadow: open ? '0 24px 60px rgba(15, 23, 42, 0.12)' : '0 12px 30px rgba(15, 23, 42, 0.08)',
        }}
      >
        {showDesktopPanel && open && (
          <Box
            onMouseDown={handleResizeStart}
            onDoubleClick={() => setPanelWidth(DEFAULT_PANEL_WIDTH)}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: RESIZE_HANDLE_WIDTH,
              cursor: 'col-resize',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, rgba(14,165,233,0.08) 0%, rgba(30,64,175,0.12) 100%)',
              borderRight: '1px solid rgba(148, 163, 184, 0.25)',
              transition: 'background 150ms ease',
              '&:hover': {
                background: 'linear-gradient(180deg, rgba(14,165,233,0.14) 0%, rgba(30,64,175,0.18) 100%)',
              },
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 72,
                borderRadius: '999px',
                background: 'repeating-linear-gradient(180deg, rgba(15,23,42,0.42) 0 4px, transparent 4px 8px)',
              }}
            />
          </Box>
        )}

        {!open ? (
          <Box
            sx={{
              height: '100%',
              minHeight: showDesktopPanel ? 560 : 'auto',
              display: 'flex',
              flexDirection: showDesktopPanel ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: 1.5,
              background: 'linear-gradient(180deg, #ecfeff 0%, #eff6ff 100%)',
            }}
          >
            <IconButton
              onClick={() => setOpen(true)}
              sx={{
                width: 48,
                height: 48,
                color: 'white',
                background: 'linear-gradient(135deg, #0f766e 0%, #1d4ed8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #115e59 0%, #1e40af 100%)',
                },
              }}
            >
              <AutoAwesomeRoundedIcon />
            </IconButton>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: '#0f172a',
                writingMode: showDesktopPanel ? 'vertical-rl' : 'horizontal-tb',
                transform: showDesktopPanel ? 'rotate(180deg)' : 'none',
                textAlign: 'center',
                letterSpacing: 0.2,
              }}
            >
              Avaa Kipinä
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              ml: showDesktopPanel ? `${RESIZE_HANDLE_WIDTH}px` : 0,
            }}
          >
            <Box
              sx={{
                p: 2.5,
                color: 'white',
                background: 'linear-gradient(160deg, #0f766e 0%, #1d4ed8 58%, #172554 100%)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <AutoAwesomeRoundedIcon />
                  <Typography variant="h5" fontWeight={700}>
                    Kipinä
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {showDesktopPanel && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(255,255,255,0.75)',
                        pr: 0.5,
                      }}
                    >
                      <DragIndicatorRoundedIcon fontSize="small" />
                      <Typography variant="caption">Vedä leveämmäksi</Typography>
                    </Box>
                  )}
                  <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Stack>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.94, mb: 1.5 }}>
                AI-apuri materiaalin ja harjoitusten suunnitteluun. Ei opiskelijachattia, vaan suoraa authoring-apua.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<DesignServicesRoundedIcon />}
                  label={focusLabel}
                  size="small"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }}
                />
              </Stack>
            </Box>

            <Box p={2}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {contextSummary}
              </Alert>
            </Box>

            <Box sx={{ px: 2, pb: 2, flex: 1, minHeight: 220, overflowY: 'auto' }}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  display="flex"
                  justifyContent={message.role === 'assistant' ? 'flex-start' : 'flex-end'}
                  mb={1.5}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '92%',
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 3,
                      backgroundColor: message.role === 'assistant' ? '#ecfeff' : '#dbeafe',
                      border: '1px solid',
                      borderColor: message.role === 'assistant' ? '#99f6e4' : '#93c5fd',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: theme.typography.body2.fontSize,
                        lineHeight: 1.6,
                        color: 'text.primary',
                        '& > *:first-of-type': { mt: 0 },
                        '& > *:last-child': { mb: 0 },
                        '& p': { my: 0.75 },
                        '& ul, & ol': { my: 0.75, pl: 2.5 },
                        '& li': { my: 0.25 },
                        '& h1, & h2, & h3, & h4': {
                          mt: 1.25,
                          mb: 0.75,
                          lineHeight: 1.3,
                          fontWeight: 700,
                        },
                        '& h1': { fontSize: theme.typography.h6.fontSize },
                        '& h2': { fontSize: theme.typography.subtitle1.fontSize },
                        '& h3, & h4': { fontSize: theme.typography.body1.fontSize },
                        '& strong': { fontWeight: 700 },
                        '& em': { fontStyle: 'italic' },
                        '& blockquote': {
                          borderLeft: '3px solid',
                          borderColor: 'primary.light',
                          m: 0,
                          pl: 1.5,
                          color: 'text.secondary',
                        },
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          my: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.45)',
                        },
                        '& th, & td': {
                          border: '1px solid rgba(14, 116, 144, 0.22)',
                          px: 1,
                          py: 0.75,
                          textAlign: 'left',
                          verticalAlign: 'top',
                        },
                        '& th': {
                          backgroundColor: 'rgba(14, 165, 233, 0.12)',
                          fontWeight: 700,
                        },
                        '& code': {
                          fontFamily: 'monospace',
                          backgroundColor: 'rgba(15, 23, 42, 0.06)',
                          px: 0.5,
                          py: 0.125,
                          borderRadius: 0.75,
                        },
                        '& pre': {
                          overflowX: 'auto',
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(15, 23, 42, 0.06)',
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(message.content) }}
                    />
                  </Paper>
                </Box>
              ))}

              {(isSending || isGeneratingDraft) && (
                <Box display="flex" alignItems="center" gap={1} px={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {isGeneratingDraft
                      ? 'Kipinä kirjoittaa luonnosta suoraan lomakkeeseen...'
                      : 'Kipinä miettii seuraavaa parasta opetuksellista liikettä...'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            <Box p={2}>
              {createActions && createActions.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Luo uutena Kipinällä
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
                    {createActions.map((action) => (
                      <Button
                        key={action.id}
                        size="small"
                        variant="contained"
                        onClick={() => action.onCreate(input.trim() || undefined)}
                        disabled={isSending || isGeneratingDraft}
                        sx={{ borderRadius: '999px' }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Stack>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Pikavaihtoehdot
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
                {quickActions.map((action) => (
                  <Button
                    key={action.id}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      void sendMessage(action.prompt);
                    }}
                    disabled={isSending || isGeneratingDraft}
                    sx={{ borderRadius: '999px' }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={input}
                onChange={(event) => setInput(event.target.value.slice(0, 2000))}
                onKeyDown={handleInputKeyDown}
                placeholder="Pyydä vaikka: suunnittele tästä materiaalista 3 tehtävää, paranna tätä tehtävänantoa tai tarkista vaikeustaso"
                helperText={`${input.length}/2000`}
                inputProps={{ maxLength: 2000 }}
              />

              {draftTarget && onApplyDraft && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    void handleGenerateDraft('create');
                  }}
                  disabled={draftDisabled || isSending || isGeneratingDraft}
                  sx={{ mt: 1.5, borderRadius: 3, py: 1.1 }}
                >
                  {resolvedDraftButtonLabel}
                </Button>
              )}

              {draftTarget && onApplyDraft && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    void handleGenerateDraft('improve');
                  }}
                  disabled={draftDisabled || isSending || isGeneratingDraft}
                  sx={{ mt: 1.5, borderRadius: 3, py: 1.1 }}
                >
                  {resolvedImproveButtonLabel}
                </Button>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isSending || isGeneratingDraft || !input.trim()}
                sx={{ mt: 1.5, borderRadius: 3, py: 1.1 }}
              >
                Kysy Kipinältä
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AuthoringAssistantPanel;
